import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";
import "@/styles/portfolio.css";

const WHATSAPP_NUMBER = "+43 664 4602202";
const WHATSAPP_LINK = "https://wa.me/436644602202";
const EMAIL = "kontakt@contentbymax.at";
const INSTAGRAM_HANDLE = "@content.by.max";
const INSTAGRAM_LINK = "https://instagram.com/content.by.max";
const GOOGLE_LINK = "https://share.google/kYNHbzuyQF4mDwJqs";

const HERO_WORDS = ["Cinematic", "Social Media", "für Brands."];

const SERVICES = [
  {
    id: "basic",
    name: "Basic",
    tagline: "Reel-Betreuung",
    description: "Verlässliche monatliche Reel-Produktion für deinen Account.",
    items: [
      "4 / 8 Reels pro Monat",
      "Content-Ideen & Planung",
      "Videodreh",
      "Schnitt / Editing",
      "Posting Service",
    ],
    price: "Preis auf Anfrage",
  },
  {
    id: "premium",
    name: "Premium",
    tagline: "Komplette Betreuung",
    description: "Volle Begleitung für Unternehmen, die wirklich wachsen wollen.",
    items: [
      "4 / 8 / 12 Reels pro Monat",
      "Content-Strategie",
      "Story-Betreuung",
      "Account-Support",
      "Posting Service",
    ],
    price: "Preis auf Anfrage",
    highlighted: true,
  },
  {
    id: "setup",
    name: "Setup",
    tagline: "Professioneller Start",
    description: "Dein kompletter Instagram-Aufbau — von Null bis Launch.",
    items: [
      "Account-Erstellung",
      "Profil-Design",
      "Bio & Positionierung",
      "Content-Grundstruktur",
      "Reel-Startpaket (8 Reels)",
    ],
    price: "Preis auf Anfrage",
  },
];

const ABOUT_PARAGRAPHS = [
  "Ich bin Max Wendl, 15 Jahre alt, aus Baden bei Wien — und ich produziere Reels, die lokale Unternehmen auf Instagram sichtbar machen.",
  "Was ich mache: Ich komme zu dir, drehe professionelles Material, schneide es zu einem Reel, das performt — und kümmere mich um Caption, Hashtags und das Posten. Du musst nichts wissen. Ich mach das.",
  "Seit ich angefangen habe, habe ich mit Restaurants, Arztpraxen und lokalen Dienstleistern zusammengearbeitet — und die Ergebnisse sprechen für sich.",
];

const NAV_LINKS = [
  { href: "#arbeit", label: "Arbeit", testid: "nav-arbeit" },
  { href: "#services", label: "Services", testid: "nav-services" },
  { href: "#ueber-mich", label: "Über mich", testid: "nav-ueber-mich" },
  { href: "/blog", label: "Blog", testid: "nav-blog", external: true },
  { href: "#kontakt", label: "Kontakt", testid: "nav-kontakt" },
];

const ROUTE_TO_SECTION = {
  "/arbeit": "arbeit",
  "/services": "services",
  "/leistungen": "services",
  "/kundenstimmen": "kundenstimmen",
  "/ueber-mich": "ueber-mich",
  "/kontakt": "kontakt",
};

const DEFAULT_TITLE = "Instagram Reels für Unternehmen in Baden bei Wien | content.by.max";

const ROUTE_TITLES = {
  "/services": "Instagram Reels & Social Media Betreuung Baden bei Wien | content.by.max",
  "/leistungen": "Instagram Reels & Social Media Betreuung Baden bei Wien | content.by.max",
};

const WhatsAppIcon = () => (
  <svg viewBox="0 0 32 32" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M16.003 3C9.374 3 4 8.373 4 14.997c0 2.36.69 4.557 1.876 6.41L4 29l7.78-1.838a11.95 11.95 0 0 0 4.222.766h.005C22.626 27.928 28 22.555 28 15.931 28 12.72 26.75 9.7 24.476 7.428A11.93 11.93 0 0 0 16.003 3Zm0 21.787h-.004a9.94 9.94 0 0 1-5.058-1.385l-.363-.215-4.616 1.09 1.108-4.5-.236-.376a9.89 9.89 0 0 1-1.524-5.404c0-5.466 4.451-9.913 9.92-9.913 2.65 0 5.14 1.033 7.013 2.905a9.84 9.84 0 0 1 2.903 7.014c0 5.466-4.452 9.784-9.92 9.784Zm5.443-7.42c-.298-.149-1.763-.87-2.036-.969-.273-.099-.472-.149-.67.149-.198.298-.768.969-.941 1.168-.173.198-.347.223-.645.074-.298-.148-1.259-.464-2.397-1.479-.886-.79-1.484-1.766-1.658-2.064-.173-.298-.018-.459.131-.607.135-.134.298-.347.447-.521.149-.173.198-.298.298-.496.099-.198.05-.372-.025-.521-.074-.149-.67-1.612-.918-2.207-.242-.581-.487-.502-.67-.51l-.57-.011a1.09 1.09 0 0 0-.793.372c-.273.298-1.041 1.018-1.041 2.481 0 1.464 1.066 2.878 1.215 3.077.149.198 2.097 3.2 5.08 4.488.71.307 1.263.49 1.694.628.712.226 1.36.194 1.872.118.572-.085 1.763-.72 2.011-1.415.248-.695.248-1.291.173-1.415-.074-.124-.273-.198-.57-.347Z" />
  </svg>
);

const InstagramIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const SoundOnIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);

const SoundOffIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="22" y1="9" x2="16" y2="15" />
    <line x1="16" y1="9" x2="22" y2="15" />
  </svg>
);

function ReelCard({ reel, idx }) {
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const tone = `showcase--${reel.tone || "warm"}`;
  const videoSrc = resolveMediaUrl(reel.video_url);
  const posterSrc = resolveMediaUrl(reel.poster_url);

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <article
      className={`showcase-card fade-up ${tone}`}
      style={{ transitionDelay: `${idx * 120}ms` }}
      data-testid={`showcase-${idx}`}
    >
      <div className="phone">
        <span className="phone__notch" />
        <div className={`phone__screen ${tone}-screen`}>
          {videoSrc ? (
            <video
              ref={videoRef}
              className="phone__video"
              src={videoSrc}
              poster={posterSrc || undefined}
              autoPlay
              loop
              muted={muted}
              playsInline
              preload="metadata"
              aria-label={reel.alt_text || reel.title}
            />
          ) : (
            <>
              <span className="phone__play">▶</span>
              <span className="phone__chip">REEL · {idx + 1}</span>
            </>
          )}
          {videoSrc && (
            <button
              type="button"
              className="reel-sound"
              onClick={toggleMute}
              aria-label={muted ? "Ton einschalten" : "Ton ausschalten"}
              data-testid={`reel-sound-${idx}`}
            >
              {muted ? <SoundOffIcon /> : <SoundOnIcon />}
            </button>
          )}
          {reel.instagram_link && (
            <a
              href={reel.instagram_link}
              target="_blank"
              rel="noopener noreferrer"
              className="reel-ig"
              aria-label={`${reel.title} auf Instagram ansehen`}
              data-testid={`reel-ig-${idx}`}
              onClick={(e) => e.stopPropagation()}
            >
              <InstagramIcon size={14} />
              <span>Instagram</span>
            </a>
          )}
        </div>
      </div>
      <span className="showcase-label">
        {reel.client ? `${reel.client}` : reel.title}
      </span>
    </article>
  );
}

function LogoBanner({ logos }) {
  if (!logos || logos.length === 0) return null;
  // Repeat the list enough times so the marquee always feels populated.
  // With 1-3 logos, duplicate 6x; with 4-6 duplicate 3x; otherwise 2x.
  const reps = logos.length <= 3 ? 6 : logos.length <= 6 ? 3 : 2;
  const looped = Array.from({ length: reps }, () => logos).flat();
  return (
    <section
      className="logo-banner"
      id="kundenlogos"
      aria-label="Vertrauensvolle Kunden von content.by.max"
      data-testid="logo-banner"
    >
      <div className="logo-banner__track">
        {looped.map((l, i) => (
          <a
            key={`${l.id}-${i}`}
            className="logo-banner__item"
            href={l.link || "#"}
            target={l.link ? "_blank" : undefined}
            rel={l.link ? "noopener noreferrer" : undefined}
            onClick={(e) => {
              if (!l.link) e.preventDefault();
            }}
            data-testid={`logo-${i % logos.length}`}
          >
            {l.image ? (
              <img
                src={l.image}
                alt={l.alt_text || l.name}
                title={l.name}
                loading="lazy"
              />
            ) : (
              <span className="logo-banner__fallback">{l.name}</span>
            )}
          </a>
        ))}
      </div>
    </section>
  );
}

function TestimonialCarousel({ testimonials }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const count = testimonials.length;

  const goTo = (i) => {
    if (count === 0) return;
    setIndex(((i % count) + count) % count);
  };
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  // Auto-rotate every 5 s (paused on hover / when user interacts)
  useEffect(() => {
    if (count <= 1 || paused) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearInterval(t);
  }, [count, paused]);

  // Reset index if the list shrinks
  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  if (count === 0) {
    return <p className="lead">Bald folgen Kundenstimmen — bleib dran.</p>;
  }

  const t = testimonials[index];

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="testimonial-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="testimonial-carousel"
    >
      {count > 1 && (
        <button
          type="button"
          className="carousel-arrow carousel-arrow--left"
          onClick={prev}
          aria-label="Vorherige Rezension"
          data-testid="testimonial-prev"
        >
          ‹
        </button>
      )}

      <div className="testimonial-slide" key={t.id}>
        <blockquote className="quote" data-testid="testimonial-quote">
          „{t.quote}"
        </blockquote>
        <p className="attribution">
          — {t.author}
          {t.title ? `, ${t.title}` : ""}
        </p>
        <div className="stars" data-testid="testimonial-stars">
          <span className="dots">
            {Array.from({ length: t.stars || 5 }).map((_, i) => (
              <span key={i} />
            ))}
          </span>
          <span className="stars__label">
            {t.stars || 5} / 5 Weiterempfehlung
          </span>
        </div>
      </div>

      {count > 1 && (
        <button
          type="button"
          className="carousel-arrow carousel-arrow--right"
          onClick={next}
          aria-label="Nächste Rezension"
          data-testid="testimonial-next"
        >
          ›
        </button>
      )}

      {count > 1 && (
        <div className="carousel-indicators" data-testid="testimonial-indicators">
          {testimonials.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`carousel-indicator${i === index ? " carousel-indicator--active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Rezension ${i + 1} anzeigen`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Portfolio() {
  const cursorDotRef = useRef(null);
  const progressBarRef = useRef(null);
  const location = useLocation();

  const [reels, setReels] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [logos, setLogos] = useState([]);

  // Load dynamic content
  useEffect(() => {
    (async () => {
      try {
        const [r, t, l] = await Promise.all([
          api.get("/reels"),
          api.get("/testimonials"),
          api.get("/logos"),
        ]);
        setReels(r.data || []);
        setTestimonials(t.data || []);
        setLogos(l.data || []);
      } catch (err) {
        // graceful: keep empty arrays — page still renders, but log for debugging
        console.warn("Portfolio: failed to load dynamic content", err);
      }
    })();
  }, []);

  // Deep-link routes + per-route document.title (SEO)
  useEffect(() => {
    document.title = ROUTE_TITLES[location.pathname] || DEFAULT_TITLE;
    const targetId = ROUTE_TO_SECTION[location.pathname];
    if (!targetId) return;
    window.scrollTo({ top: 0, behavior: "instant" });
    const t = setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Custom cursor + scroll progress
  useEffect(() => {
    const dot = cursorDotRef.current;
    const bar = progressBarRef.current;
    if (!dot || !bar) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let dotX = mouseX;
    let dotY = mouseY;
    let rafId;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    const onEnterLink = () => dot.classList.add("cursor--hover");
    const onLeaveLink = () => dot.classList.remove("cursor--hover");
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      bar.style.width = `${pct}%`;
    };
    const loop = () => {
      dotX += (mouseX - dotX) * 0.18;
      dotY += (mouseY - dotY) * 0.18;
      dot.style.transform = `translate3d(${dotX}px, ${dotY}px, 0) translate(-50%, -50%)`;
      rafId = requestAnimationFrame(loop);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("scroll", onScroll, { passive: true });
    const interactive = document.querySelectorAll(
      "a, button, .service-card, .showcase-card, .contact-row"
    );
    interactive.forEach((el) => {
      el.addEventListener("mouseenter", onEnterLink);
      el.addEventListener("mouseleave", onLeaveLink);
    });

    loop();
    onScroll();
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", onScroll);
      interactive.forEach((el) => {
        el.removeEventListener("mouseenter", onEnterLink);
        el.removeEventListener("mouseleave", onLeaveLink);
      });
    };
  }, [reels, logos]);

  // Intersection Observer
  useEffect(() => {
    const els = document.querySelectorAll(
      ".fade-up, .fade-left, .fade-right, .fade-in"
    );
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [reels, testimonials, logos]);

  useEffect(() => {
    const t = setTimeout(() => {
      document.querySelectorAll(".hero-word").forEach((el) => el.classList.add("visible"));
      document.querySelectorAll(".hero-tail").forEach((el) => el.classList.add("visible"));
    }, 80);
    return () => clearTimeout(t);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="portfolio-root" data-testid="portfolio-root">
      <div className="cursor-dot" ref={cursorDotRef} aria-hidden="true" data-testid="custom-cursor" />
      <div className="scroll-progress" ref={progressBarRef} aria-hidden="true" data-testid="scroll-progress" />

      {/* HERO */}
      <section className="section section--dark hero" id="hero">
        <header className="top-bar">
          <a href="#hero" className="logo" onClick={(e) => handleNavClick(e, "#hero")} data-testid="logo-link">
            content.by.max
          </a>
          <nav className="nav" data-testid="main-nav">
            {NAV_LINKS.map((link) =>
              link.external ? (
                <Link key={link.href} to={link.href} data-testid={link.testid}>
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  data-testid={link.testid}
                >
                  {link.label}
                </a>
              )
            )}
          </nav>
        </header>

        <div className="hero__inner">
          <h1 className="hero__headline" data-testid="hero-headline">
            {HERO_WORDS.map((word, idx) => (
              <span key={word} className="hero-word" style={{ transitionDelay: `${idx * 150}ms` }}>
                {word}
              </span>
            ))}
          </h1>
          <p className="hero__sub hero-tail" style={{ transitionDelay: "600ms" }}>
            Ich produziere Instagram Reels für Unternehmen in Baden bei Wien, Wien und Umgebung — professionell, lokal, messbar.
          </p>
          <a
            href="#kontakt"
            onClick={(e) => handleNavClick(e, "#kontakt")}
            className="btn btn--ghost hero-tail"
            style={{ transitionDelay: "780ms" }}
            data-testid="hero-cta"
          >
            <span>Jetzt anfragen</span>
            <span className="btn__arrow">→</span>
          </a>
        </div>

        <div className="scroll-indicator" aria-hidden="true">
          <span className="scroll-indicator__line" />
          <span className="scroll-indicator__label">scroll</span>
        </div>
      </section>

      {/* ARBEIT */}
      <section className="section section--light work" id="arbeit">
        <div className="rotated-eyebrow" aria-hidden="true">01 — ARBEIT</div>
        <div className="section__inner">
          <span className="eyebrow eyebrow--mobile">01 — ARBEIT</span>
          <h2 className="display fade-left" data-testid="work-title">Echte Ergebnisse.</h2>
          <p className="lead fade-up" style={{ transitionDelay: "120ms" }} data-testid="work-subtitle">
            Reels, die nicht nur gut aussehen — sondern performen.
          </p>

          <div className="showcase-grid">
            {reels.length > 0
              ? reels.map((r, idx) => <ReelCard key={r.id} reel={r} idx={idx} />)
              : null}
          </div>

          <p className="note fade-in" style={{ transitionDelay: "320ms" }}>
            <em>Echte Kundenprojekte — Videomaterial auf Anfrage.</em>
          </p>
        </div>
        <div className="section-divider section-divider--down" aria-hidden />
      </section>

      {/* SERVICES */}
      <section className="section section--dark services" id="services">
        <div className="section__inner section__inner--center">
          <span className="eyebrow fade-up">02 — SERVICES</span>
          <h2 className="display fade-up" style={{ transitionDelay: "100ms" }} data-testid="services-title">
            Was du bekommst.
          </h2>
          <p className="lead lead--muted fade-up" style={{ transitionDelay: "180ms" }}>
            Instagram Reels und Social Media Betreuung für Unternehmen in Baden bei Wien, Wien und Niederösterreich — drei Pakete, alle individuell anpassbar.
          </p>

          <div className="service-grid">
            {SERVICES.map((s, idx) => (
              <article
                key={s.id}
                className={`service-card fade-up ${s.highlighted ? "service-card--highlight" : ""}`}
                style={{ transitionDelay: `${idx * 140}ms` }}
                data-testid={`service-${s.id}`}
              >
                <header className="service-card__head">
                  <span className="service-card__eyebrow">{s.tagline}</span>
                  <h3 className="service-card__name">{s.name}</h3>
                  <p className="service-card__desc">{s.description}</p>
                </header>
                <ul className="service-card__list">
                  {s.items.map((item) => (
                    <li key={item}>
                      <span className="dash">→</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <footer className="service-card__foot">
                  <span className="service-card__price">{s.price}</span>
                </footer>
              </article>
            ))}
          </div>

          <div className="custom-offer fade-up" style={{ transitionDelay: "520ms" }} data-testid="custom-offer-block">
            <div className="custom-offer__inner">
              <span className="eyebrow custom-offer__eyebrow">Nichts passt zu 100%?</span>
              <h3 className="custom-offer__title">
                Dann bauen wir dein <span className="italic">eigenes</span> Paket.
              </h3>
              <p className="custom-offer__text">
                Jedes dieser Pakete ist individuell anpassbar — mehr Reels, weniger Reels, andere Schwerpunkte,
                ein einzelnes Event-Reel, Hybrid-Setups: kein Problem. Wenn du etwas Spezifisches im Kopf hast
                oder unsicher bist, was zu deinem Business passt, schreib mir kurz. Du bekommst innerhalb von
                24 Stunden ein <strong>persönliches Angebot</strong> — kostenlos, unverbindlich und exakt auf
                dein Unternehmen zugeschnitten.
              </p>
              <div className="custom-offer__actions">
                <a
                  href="#kontakt"
                  onClick={(e) => handleNavClick(e, "#kontakt")}
                  className="btn btn--solid"
                  data-testid="custom-offer-cta"
                >
                  <span>Persönliches Angebot anfragen</span>
                  <span className="btn__arrow">→</span>
                </a>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--ghost custom-offer__alt"
                  data-testid="custom-offer-whatsapp"
                >
                  <span>Direkt via WhatsApp</span>
                  <span className="btn__arrow">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGO BANNER — slim trust strip between dark services and light testimonial */}
      <LogoBanner logos={logos} />

      {/* TESTIMONIAL */}
      <section className="section section--light testimonial" id="kundenstimmen">
        <span className="quote-mark" aria-hidden="true">“</span>
        <div className="section__inner section__inner--center">
          <span className="eyebrow fade-up">03 — KUNDENSTIMME</span>
          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* ÜBER MICH */}
      <section className="section section--dark about" id="ueber-mich">
        <div className="about__grid">
          <div className="about__photo fade-left" data-testid="about-photo">
            <div className="photo-frame">
              <img
                src="/images/max-wendl-content-creator-baden-bei-wien.jpg"
                alt="Max Wendl — Instagram Reels Producer aus Baden bei Wien, Niederösterreich"
                className="photo-img"
                loading="lazy"
              />
            </div>
            <span className="photo-tag">Baden bei Wien · NÖ</span>
          </div>

          <div className="about__body">
            <span className="eyebrow fade-up">04 — ÜBER MICH</span>
            <h2 className="display display--name fade-up" style={{ transitionDelay: "100ms" }} data-testid="about-name">
              Max.
            </h2>

            {ABOUT_PARAGRAPHS.map((p, i) => (
              <p
                key={i}
                className="about__text fade-up"
                style={{ transitionDelay: `${200 + i * 120}ms` }}
              >
                {p}
              </p>
            ))}

            <div className="stats fade-up" style={{ transitionDelay: "640ms" }}>
              <div className="stat" data-testid="stat-projects">
                <span className="stat__num">15+</span>
                <span className="stat__label">Kundenprojekte</span>
              </div>
              <div className="stat" data-testid="stat-care">
                <span className="stat__num">100%</span>
                <span className="stat__label">Rundum-Betreuung</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOG TEASER */}
      <section className="section section--light blog-teaser" id="blog">
        <div className="section__inner blog-teaser__inner">
          <span className="eyebrow fade-up">05 — JOURNAL</span>
          <h2 className="display fade-up" style={{ transitionDelay: "100ms" }} data-testid="blog-teaser-title">
            Gedanken aus der <span className="blog-teaser__italic">Praxis</span>.
          </h2>
          <p
            className="lead fade-up blog-teaser__lead"
            style={{ transitionDelay: "180ms" }}
            data-testid="blog-teaser-lead"
          >
            Hier findest du Tipps zu Instagram Reels, Social Media Marketing und Content Creation für kleine
            Unternehmen in Baden, Wien und Umgebung. Ich schreibe über Themen, die für Restaurants, Cafés und
            lokale Dienstleister wirklich relevant sind — von der Idee bis zur Umsetzung.
          </p>
          <Link
            to="/blog"
            className="btn btn--solid fade-up"
            style={{ transitionDelay: "300ms" }}
            data-testid="blog-teaser-cta"
          >
            <span>Zu meinem Blog</span>
            <span className="btn__arrow">→</span>
          </Link>
        </div>
        {/* Zickzag transition into Kontakt section */}
        <div className="zigzag-divider zigzag-divider--down" aria-hidden="true" data-testid="zigzag-divider">
          <svg viewBox="0 0 1200 80" preserveAspectRatio="none">
            <polygon
              points="0,0 100,40 200,0 300,40 400,0 500,40 600,0 700,40 800,0 900,40 1000,0 1100,40 1200,0 1200,80 0,80"
              fill="currentColor"
            />
          </svg>
        </div>
      </section>

      {/* KONTAKT — now DARK for true contrast against light Journal section */}
      <section className="section section--dark contact" id="kontakt">
        <span className="bg-arrow" aria-hidden="true">→</span>
        <div className="section__inner section__inner--center">
          <span className="eyebrow fade-up">06 — KONTAKT</span>
          <h2 className="display fade-up" style={{ transitionDelay: "100ms" }} data-testid="contact-title">
            <span className="split-left">Bereit, sichtbar</span>{" "}
            <span className="split-right">zu werden?</span>
          </h2>
          <p className="lead fade-up" style={{ transitionDelay: "180ms" }}>
            Schreib mir — kostenloses Erstgespräch, kein Druck, kein Smalltalk-Overhead.
          </p>

          <div className="contact-list">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-row fade-up"
              style={{ transitionDelay: "80ms" }}
              data-testid="contact-whatsapp"
            >
              <span className="contact-row__icon contact-row__icon--logo"><WhatsAppIcon /></span>
              <span className="contact-row__label">WhatsApp</span>
              <span className="contact-row__value">{WHATSAPP_NUMBER}</span>
              <span className="contact-row__arrow">→</span>
            </a>
            <a
              href={`mailto:${EMAIL}`}
              className="contact-row fade-up"
              style={{ transitionDelay: "160ms" }}
              data-testid="contact-email"
            >
              <span className="contact-row__icon">@</span>
              <span className="contact-row__label">E-Mail</span>
              <span className="contact-row__value">{EMAIL}</span>
              <span className="contact-row__arrow">→</span>
            </a>
            <a
              href={GOOGLE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-row fade-up"
              style={{ transitionDelay: "240ms" }}
              data-testid="contact-location"
            >
              <span className="contact-row__icon">◉</span>
              <span className="contact-row__label">Standort</span>
              <span className="contact-row__value">Baden bei Wien, NÖ</span>
              <span className="contact-row__arrow">→</span>
            </a>
            <a
              href={INSTAGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-row fade-up"
              style={{ transitionDelay: "320ms" }}
              data-testid="contact-instagram"
            >
              <span className="contact-row__icon contact-row__icon--logo"><InstagramIcon /></span>
              <span className="contact-row__label">Instagram</span>
              <span className="contact-row__value">{INSTAGRAM_HANDLE}</span>
              <span className="contact-row__arrow">→</span>
            </a>
          </div>

          <p className="legal fade-in" style={{ transitionDelay: "480ms" }} data-testid="legal-note">
            content.by.max ist ein Kleinunternehmen nach österreichischem Recht.
          </p>
        </div>

        <footer className="footer">
          <span>© {new Date().getFullYear()} content.by.max</span>
          <span className="footer__dot">·</span>
          <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram">
            {INSTAGRAM_HANDLE}
          </a>
        </footer>
      </section>
    </div>
  );
}
